import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Périmètre de consultation des données, indépendant du rôle.
 *
 * - `mine` : uniquement la patientèle du praticien — vue nominative, drill-down
 *   patient autorisé, effectifs exacts.
 * - `specialite` : toute la spécialité, en vue anonymisée — noms masqués, pas de
 *   drill-down, et k-anonymat appliqué côté backend (petits effectifs masqués).
 *
 * Le masquage découle du périmètre : il n'y a pas de réglage séparé à oublier.
 */
export type DataScope = "mine" | "specialite";

interface ScopeState {
  scope: DataScope;
  /**
   * Whether the speciality on screen opens beyond the reader's patientèle at all — the CNIL
   * authorised the warehouse per therapeutic area. The board says so (`/dimensions`); outside it,
   * there is no « Toute la spécialité » to choose.
   */
  reuseAuthorised: boolean;
  setScope: (scope: DataScope) => void;
  setReuseAuthorised: (reuseAuthorised: boolean) => void;
}

export const useScope = create<ScopeState>()(
  persist(
    (set) => ({
      scope: "mine",
      reuseAuthorised: true,
      setScope: (scope) => set({ scope }),
      setReuseAuthorised: (reuseAuthorised) =>
        set((state) => ({
          reuseAuthorised,
          scope: reuseAuthorised ? state.scope : "mine",
        })),
    }),
    {
      name: "mage-x-data-scope",
      version: 1,
      // The authorisation is the server's answer for the speciality on screen, never remembered.
      partialize: (state) => ({ scope: state.scope }),
    },
  ),
);

/** Vrai lorsque le périmètre impose l'anonymisation (données inter-praticiens). */
export function isAnonymisedScope(scope: DataScope): boolean {
  return scope === "specialite";
}
